// Organization types for teams. Stored in teams.org_type; teams.org_type_detail
// holds a free-text description when org_type = 'other' (analytics only — never
// used for branching or shown back in the product UI).
//
// IMPORTANT: org type never changes what is STORED for member roles — the
// team_role enum values ('player' | 'coach' | 'staff') are written for every
// org type. Org type only changes the words shown to the user.

export const ORG_TYPE_OTHER = 'other';

export const ORG_TYPE_OPTIONS = Object.freeze([
  {
    value: 'college',
    label: 'College / University',
    icon: 'GraduationCap',
    description: 'Collegiate teams and athletic programs',
  },
  {
    value: 'high_school',
    label: 'High School',
    icon: 'School',
    description: 'High school teams and programs',
  },
  {
    value: 'club_sports',
    label: 'Club Sports',
    icon: 'Trophy',
    description: 'Club, travel, and rec league teams',
  },
  {
    value: ORG_TYPE_OTHER,
    label: 'Other',
    icon: 'Users',
    description: 'Any other group that eats together',
  },
]);

export const ORG_TYPE_VALUES = Object.freeze(ORG_TYPE_OPTIONS.map((o) => o.value));

const LABEL_BY_VALUE = ORG_TYPE_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

export const normalizeOrgType = (orgType) => {
  const key = typeof orgType === 'string' ? orgType.trim().toLowerCase() : '';
  // Teams that predate org types were all created through the college-only flow.
  return ORG_TYPE_VALUES.includes(key) ? key : 'college';
};

export const getOrgTypeLabel = (orgType) => LABEL_BY_VALUE[normalizeOrgType(orgType)];

// Sports-style orgs get athletics vocabulary (Coach/Player, Sport/Gender fields);
// 'other' orgs get neutral vocabulary (Organizer/Member) and no athletics fields.
export const isSportsOrg = (orgType) => normalizeOrgType(orgType) !== ORG_TYPE_OTHER;

// Field visibility per org type. Conference applies to school athletics
// (required for college, optional for high school); club teams don't have one.
export const orgShowsSportFields = (orgType) => isSportsOrg(orgType);
export const orgShowsConference = (orgType) =>
  ['college', 'high_school'].includes(normalizeOrgType(orgType));
export const orgRequiresConference = (orgType) => normalizeOrgType(orgType) === 'college';
